import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ZodError } from "zod";
import { companySchema, type CompanyValues } from "./schemas";

interface Props {
  values: CompanyValues;
  onChange: (next: CompanyValues) => void;
  errors: Partial<Record<keyof CompanyValues, string>>;
}

export default function StepCompany({ values, onChange, errors }: Props) {
  const update = (key: keyof CompanyValues, v: string) =>
    onChange({ ...values, [key]: v });

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        This information appears in generated reports and on your dashboard
        greeting.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="setup-name">
            Your name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="setup-name"
            value={values.displayName}
            onChange={(e) => update("displayName", e.target.value)}
            placeholder="Jane Martinez"
            maxLength={100}
            aria-invalid={!!errors.displayName}
          />
          {errors.displayName && (
            <p className="text-xs text-destructive">{errors.displayName}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="setup-company">Company</Label>
          <Input
            id="setup-company"
            value={values.company ?? ""}
            onChange={(e) => update("company", e.target.value)}
            placeholder="EnviroEng Pro"
            maxLength={120}
            aria-invalid={!!errors.company}
          />
          {errors.company && (
            <p className="text-xs text-destructive">{errors.company}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="setup-license">PE license</Label>
          <Input
            id="setup-license"
            value={values.peLicense ?? ""}
            onChange={(e) => update("peLicense", e.target.value)}
            placeholder="OR-12345"
            maxLength={40}
            aria-invalid={!!errors.peLicense}
          />
          {errors.peLicense && (
            <p className="text-xs text-destructive">{errors.peLicense}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="setup-office">Office</Label>
          <Input
            id="setup-office"
            value={values.office ?? ""}
            onChange={(e) => update("office", e.target.value)}
            placeholder="Portland, OR"
            maxLength={80}
            aria-invalid={!!errors.office}
          />
          {errors.office && (
            <p className="text-xs text-destructive">{errors.office}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function validateCompany(v: CompanyValues) {
  try {
    companySchema.parse(v);
    return { ok: true as const, errors: {} };
  } catch (e) {
    if (e instanceof ZodError) {
      const errors: Partial<Record<keyof CompanyValues, string>> = {};
      for (const issue of e.issues) {
        const k = issue.path[0] as keyof CompanyValues;
        if (!errors[k]) errors[k] = issue.message;
      }
      return { ok: false as const, errors };
    }
    return { ok: false as const, errors: { displayName: "Invalid input" } };
  }
}
